import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import _ from "lodash";
import cx from "classnames";

import styles from "./create.module.scss";
import { Form, Grid, Modal } from "semantic-ui-react";

import { createUser } from "actions/users";
import SaveChanges from "components/ui/Buttons/SaveButton";
import Cancel from "components/ui/Buttons/CancelButton";
import { PasswordValidator } from "components/ui/Validator/Password/PasswordValidator";

const initUser = {
  firstName: null,
  lastName: null,
  email: null,
  userName: null,
  mobilePhone: null,
  role: null,
};

const initErrorFields = [
  {
    name: "firstName",
    title: "First Name",
    error: false,
    message: null,
  },
  {
    name: "lastName",
    title: "Last Name",
    error: false,
    message: null,
  },
  {
    name: "email",
    title: "Email",
    error: false,
    message: null,
  },
  {
    name: "userName",
    title: "User Name",
    error: false,
    message: null,
  },
  {
    name: "mobilePhone",
    title: "Phone",
    error: false,
    message: null,
  },
  {
    name: "password",
    title: "Password",
    error: false,
    message: null,
  },

  {
    name: "role",
    title: "Role",
    error: false,
    message: null,
  },
];

const roleOptions = [
  { key: 0, text: "Administrator", value: "administrator" },
  { key: 1, text: "Content Publisher", value: "publisher" },
  { key: 2, text: "BI Analyst", value: "bi_analyst" },
  { key: 3, text: "Normal User", value: "normal_user" },
];

export default function CreateUser({ account, open, setOpen }) {
  const dispatch = useDispatch();

  const [initialRendering, setInitialRendering] = useState(true);

  const [user, setUser] = useState(initUser);

  const [error, setError] = useState(null);
  const [errorFields, setErrorFields] = useState(initErrorFields);

  const [isFormSubmitted, setFormSubmitted] = useState(false);

  const creatingUser = useSelector((state) =>
    _.get(state, "users.creatingUser")
  );

  const validateUsername = (username) => {
    return /^\S+$/.test(username);
  };

  useEffect(() => {
    setUser({ ...initUser });
    setErrorFields([...initErrorFields]);
    setError(null);
    setFormSubmitted(false);
  }, [open]);

  useEffect(() => {
    validateFields();

    if (isFormSubmitted) {
      // reset form if user trying to resets the value
      setFormSubmitted(false);
    }
  }, [user]);

  useEffect(() => {
    if (!initialRendering && creatingUser && !creatingUser.creating) {
      if (creatingUser.success) {
        setOpen(false);
        toast.success(`${user.firstName} created successfully`);
      } else if (creatingUser.error) {
        setError(creatingUser.error);
      }
    }
  }, [creatingUser]);

  useEffect(() => {
    let message = null;
    for (var i = 0; i < errorFields.length; i++) {
      if (errorFields[i].error) {
        message = errorFields[i].message;
        break;
      }
    }

    setError(message);
  }, [errorFields]);

  setTimeout(() => {
    setInitialRendering(false);
  }, 3000);

  const changeToLowerCase= (email) => {
    setUser({ ...user, email: email.toLowerCase()})
  }

  const isFieldError = (fieldName) => {
    let error = false;
    if (isFormSubmitted) {
      errorFields.forEach((field, idx) => {
        if (field.name === fieldName && field.error) {
          error = true;
        }
      });
    }

    return error;
  };

  const isFormValid = () => {
    let errors = errorFields.filter((item) => item.error);
    if (errors.length > 0) return false;

    return true;
  };

  const validateFields = () => {
    let fields = [];
    for (var i = 0; i < errorFields.length; i++) {
      let field = errorFields[i].name;
      let value = user[field];

      errorFields[i].error =
        value === null || value === undefined ? true : false;

      if (!value) {
        errorFields[i].error = true;
        errorFields[i].message = errorFields[i].title + " is required";
      } else {
        errorFields[i].error = false;
        errorFields[i].message = null;
      }
      if (field === "userName" && value) {
      if (!validateUsername(value)) {
        errorFields[i].error = true;
        errorFields[i].message =
          "Username cannot contain spaces";
      }
    }

      if (field === "password" && value) {
        let message = PasswordValidator(value);
        if (message) {
          errorFields[i].error = true;
          errorFields[i].message = message;
        }
      }

      fields.push(errorFields[i]);
    }

    setErrorFields(fields);
  };

  const onCreateUser = (e) => {
    e.preventDefault();

    if (e.type === "submit") {
      setFormSubmitted(true);

      if (isFormValid()) {
        dispatch(createUser({ accountId: account.id, user: user }));
      }
    }
  };

  return (
    <>
      <Modal
        size="tiny"
        open={open}
        centered={false}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        className={cx(styles.create)}
      >
        <Modal.Content className={cx(styles.content)}>
          <Form onSubmit={onCreateUser}>
            <Grid>
              <Grid.Row>
                <Grid.Column textAlign="center">
                  <span className={cx(styles.title)}> create user </span>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    fluid
                    id="firstName"
                    label={"First Name"}
                    placeholder="First Name"
                    value={user.firstName}
                    onChange={(e) =>
                      setUser({ ...user, firstName: e.target.value })
                    }
                    error={isFieldError("firstName")}
                  ></Form.Input>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    fluid
                    id="lastName"
                    label={"Last Name"}
                    placeholder="Last Name"
                    value={user.lastName}
                    onChange={(e) =>
                      setUser({ ...user, lastName: e.target.value })
                    }
                    error={isFieldError("lastName")}
                  ></Form.Input>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    fluid
                    id="email"
                    label={"Email"}
                    placeholder="Email"
                    value={user.email}
                    onChange={(e) =>
                      setUser({ ...user, email: e.target.value })
                    }
                    onBlur={(e) => {
                      changeToLowerCase(e.target.value)
                    }}
                    error={isFieldError("email")}
                  ></Form.Input>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
         <Form.Input
      fluid
      id="userName"
      label={"User Name"}
      placeholder="User Name"
      value={user.userName}
      onChange={(e) =>
        setUser({ ...user, userName: e.target.value })
      }
      error={isFieldError("userName")}
    />
  </Grid.Column>
</Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    fluid
                    id="mobilePhone"
                    label={"Phone"}
                    placeholder="Phone"
                    value={user.mobilePhone}
                    onChange={(e) =>
                      setUser({ ...user, mobilePhone: e.target.value })
                    }
                    error={isFieldError("mobilePhone")}
                  ></Form.Input>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    fluid
                    id="password"
                    label={"Password"}
                    type="password"
                    placeholder="Password"
                    value={user.password}
                    onChange={(e) =>
                      setUser({ ...user, password: e.target.value })
                    }
                    error={isFieldError("password")}
                  ></Form.Input>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Dropdown
                    id="role"
                    label="Role"
                    placeholder="Role"
                    value={user.role}
                    onChange={(e, data) => {
                      setUser({ ...user, role: data.value });
                    }}
                    selection
                    options={roleOptions}
                    error={isFieldError("role")}
                  ></Form.Dropdown>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row columns={2}>
                <Grid.Column>
                  {isFormSubmitted && (
                    <div className={cx(styles.error)}> {error}</div>
                  )}
                </Grid.Column>
                <Grid.Column textAlign="right">
                  <SaveChanges content="Create" type="submit" />
                  <Cancel content="Cancel" onClick={() => setOpen(false)} />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        </Modal.Content>
      </Modal>
    </>
  );
}
